import React from 'react';

// Ride statuses
const rideStages = [
  { key: 'request_sent', label: 'Request Sent', icon: '📡' },
  { key: 'driver_assigned', label: 'Driver Assigned', icon: '✓' },
  { key: 'picked_up', label: 'Picked Up', icon: '🚗' },
  { key: 'in_transit', label: 'In Transit', icon: '🛣️' },
  { key: 'delivered', label: 'Completed', icon: '🏁' },
];

// Delivery statuses
const deliveryStages = [
  { key: 'request_sent', label: 'Request Sent', icon: '📡' },
  { key: 'driver_assigned', label: 'Rider Assigned', icon: '✓' },
  { key: 'picked_up', label: 'Package Picked Up', icon: '📦' },
  { key: 'in_transit', label: 'In Transit', icon: '🛵' },
  { key: 'delivered', label: 'Delivered', icon: '✅' },
];

// Map legacy statuses to new ones
const normalizeStatus = (status) => {
  if (!status) return 'request_sent';
  const map = {
    pending: 'request_sent',
    confirmed: 'driver_assigned',
    en_route: 'in_transit',
  };
  return map?.[status] || status;
};

const OrderStatusTimeline = ({ currentStatus = 'request_sent', serviceType = 'ride' }) => {
  const stages = serviceType === 'delivery' ? deliveryStages : rideStages;
  const normalized = normalizeStatus(currentStatus);
  const currentIndex = stages?.findIndex(s => s?.key === normalized);
  const effectiveIndex = currentIndex === -1 ? 0 : currentIndex;

  return (
    <div className="bg-white rounded-2xl p-4 border border-[#0a1628]/8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-bold text-[#0a1628]/40 uppercase tracking-wider">
          {serviceType === 'delivery' ? 'Delivery Status' : 'Order Status'}
        </h3>
        {serviceType === 'delivery' && (
          <span className="text-xs font-bold text-blue-500 bg-blue-500/10 px-2 py-0.5 rounded-full">📦 Delivery</span>
        )}
      </div>
      <div className="relative">
        {/* Progress line background */}
        <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-[#0a1628]/10 rounded-full" />
        {/* Progress line fill */}
        <div
          className="absolute left-4 top-4 w-0.5 rounded-full transition-all duration-700"
          style={{
            height: `${(effectiveIndex / (stages?.length - 1)) * 100}%`,
            background: serviceType === 'delivery' ? '#3b82f6' : '#ffd700',
          }}
        />

        <div className="space-y-4">
          {stages?.map((stage, i) => {
            const isCompleted = i < effectiveIndex;
            const isActive = i === effectiveIndex;

            return (
              <div key={stage?.key} className="flex items-center gap-3 relative">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm z-10 flex-shrink-0 transition-all duration-500 ${
                  isCompleted
                    ? serviceType === 'delivery' ? 'bg-blue-500 text-white shadow-md' : 'bg-[#ffd700] text-[#0a1628] shadow-md'
                    : isActive
                    ? 'bg-[#0a1628] text-[#ffd700] shadow-lg ring-4 ring-[#ffd700]/20'
                    : 'bg-[#0a1628]/8 text-[#0a1628]/30'
                }`}>
                  {isCompleted ? (
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                  ) : (
                    <span className="text-xs">{stage?.icon}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${
                    isActive ? 'text-[#0a1628]' :
                    isCompleted ? 'text-[#0a1628]/70' : 'text-[#0a1628]/30'
                  }`}>
                    {stage?.label}
                  </p>
                </div>
                {isActive && (
                  <div className="flex items-center gap-1 bg-[#ffd700]/15 rounded-full px-2.5 py-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#ffd700] animate-pulse" />
                    <span className="text-xs font-semibold text-[#0a1628]">Now</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OrderStatusTimeline;
